import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMutation, useQuery } from '@apollo/client';
import { GetChecklistVersionsInUseQuery } from '../__generated__/graphql';
import { revisionTypeToShortText } from './RevisionModel';
import { checklistTitle, ChecklistTemplate } from './checklistTemplateModel';
import { StyledPaper } from '../theme';
import { DEFINE_VERSION_MAPPING, GET_CHECKLIST_VERSIONS_IN_USE, MIGRATE_REVISIONS_ON_VERSION } from './checklistTemplate.graphql';

const sep = '\u001f';

const dropped = 'DROPPED';

type Decisions = Record<string, string | typeof dropped>;

const taskKey = (phase: string | null | undefined, name: string | null | undefined) =>
  [(phase ?? '').trim().toLowerCase(), (name ?? '').trim().toLowerCase()].join(sep);

export const ChecklistVersionMigration: FC<{ template: ChecklistTemplate | null | undefined; canEdit: boolean }> = ({ template, canEdit }) => {
  const { data, loading, error } = useQuery(GET_CHECKLIST_VERSIONS_IN_USE, { fetchPolicy: 'cache-and-network' });
  const versions = (data?.checklistVersionsInUse ?? []).filter((x) => !!x);

  const [moved, setMoved] = useState<{ count: number; toVersion: string } | null>(null);

  if (loading && versions.length === 0) return null;

  return (
    <StyledPaper>
      <Typography variant="h5" component="h2">
        Flytt revisjoner til versjon {template?.version}
      </Typography>
      {error ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Fikk ikke hentet hvilke versjoner revisjonene ligger på.
        </Alert>
      ) : (
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
          {versions.length === 0
            ? 'Alle pågående revisjoner ligger på gjeldende versjon.'
            : 'Koblingen bestemmes én gang per versjon og gjelder alle revisjonene som ligger på den. Punkter som allerede er utført, følger med dit koblingen peker.'}
        </Typography>
      )}

      {moved && (
        <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setMoved(null)}>
          {moved.count} {moved.count === 1 ? 'revisjon er' : 'revisjoner er'} flyttet til {moved.toVersion}.
        </Alert>
      )}

      {versions.map((version) => (
        <VersionRow key={version.version} version={version} template={template} canEdit={canEdit} onMoved={setMoved} />
      ))}
    </StyledPaper>
  );
};

type Version = NonNullable<NonNullable<GetChecklistVersionsInUseQuery['checklistVersionsInUse']>[number]>;

const VersionRow: FC<{
  version: Version;
  template: ChecklistTemplate | null | undefined;
  canEdit: boolean;
  onMoved: (result: { count: number; toVersion: string }) => void;
}> = ({ version, template, canEdit, onMoved }) => {
  const [defineMapping, { loading: saving }] = useMutation(DEFINE_VERSION_MAPPING);
  const [error, setError] = useState<string | null>(null);

  const tasks = useMemo(() => (version.tasks ?? []).filter((x) => !!x), [version.tasks]);

  const initial = useMemo<Decisions>(() => {
    const pairs = version.mapping?.pairs ?? version.suggestedPairs ?? [];
    const stored = !!version.mapping;

    return pairs
      .filter((x) => !!x)
      .reduce<Decisions>((all, pair) => {
        const key = taskKey(pair.fromPhase, pair.fromName);
        if (pair.toName) return { ...all, [key]: [pair.toPhase ?? '', pair.toName].join(sep) };
        return stored ? { ...all, [key]: dropped } : all;
      }, {});
  }, [version.mapping, version.suggestedPairs]);

  const [decisions, setDecisions] = useState<Decisions>(initial);

  // Keyed on content: a refetch gives every row new objects, which must not wipe unsaved choices.
  const initialContent = JSON.stringify(initial);
  useEffect(() => setDecisions(JSON.parse(initialContent)), [initialContent]);

  const targets = useMemo(() => targetsByType(template), [template]);
  const undecided = tasks.filter((task) => !decisions[taskKey(task.phase, task.name)]);
  const blocking = undecided.filter((task) => task.completedCount > 0);

  const edited = JSON.stringify(decisions) !== JSON.stringify(initial);

  const canSave = !version.mapping || edited;

  const handleSave = async () => {
    setError(null);

    const pairs = tasks.map((task) => {
      const decision = decisions[taskKey(task.phase, task.name)];
      const [toPhase, toName] = decision && decision !== dropped ? decision.split(sep) : [null, null];
      return { fromPhase: task.phase, fromName: task.name, toPhase: toPhase || null, toName };
    });

    const decided = pairs.filter((pair) => !!decisions[taskKey(pair.fromPhase, pair.fromName)]);

    const result = await defineMapping({
      variables: { input: { fromVersion: version.version, pairs: decided } },
      refetchQueries: [{ query: GET_CHECKLIST_VERSIONS_IN_USE }],
    });
    const failure = result.data?.checklistTemplateDefineVersionMapping?.errors?.[0];
    if (failure) setError('message' in failure ? failure.message : 'Kunne ikke lagre koblingen.');
  };

  return (
    <Accordion disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ width: '100%', pr: 1 }}>
          <Typography sx={{ fontWeight: 500 }}>Versjon {version.version}</Typography>
          <Typography variant="body2" color="textSecondary">
            {version.revisions} {version.revisions === 1 ? 'revisjon' : 'revisjoner'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {blocking.length > 0 ? (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={`${blocking.length} ${blocking.length === 1 ? 'punkt' : 'punkter'} med utført arbeid mangler kobling`}
            />
          ) : version.mapping ? (
            <Chip size="small" color="success" variant="outlined" label="Klar til å flyttes" />
          ) : (
            <Chip size="small" color="info" variant="outlined" label="Koblingen er ikke lagret ennå" />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Punkt på {version.version}</TableCell>
              <TableCell width={110}>Utført i</TableCell>
              <TableCell width={320}>Flyttes til</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((task) => {
              const key = taskKey(task.phase, task.name);
              return (
                <TableRow key={key}>
                  <TableCell>
                    <Typography variant="body2">{task.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {[task.phase ?? checklistTitle, (task.types ?? []).map(revisionTypeToShortText).join(', ')].filter(Boolean).join(' · ')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={task.completedCount > 0 ? 'warning.main' : 'textSecondary'}>
                      {task.completedCount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      variant="standard"
                      disabled={!canEdit || saving}
                      value={decisions[key] ?? ''}
                      onChange={(event) => setDecisions({ ...decisions, [key]: event.target.value })}>
                      <MenuItem value="" disabled>
                        Ikke bestemt
                      </MenuItem>
                      {(task.phase ? targets.activities : targets.checkItems).map((target) => (
                        <MenuItem key={target.value} value={target.value}>
                          {target.label}
                        </MenuItem>
                      ))}
                      <MenuItem value={dropped}>Utgår</MenuItem>
                    </TextField>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {canEdit && (
          <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" sx={{ mt: 2 }} useFlexGap flexWrap="wrap">
            <MoveAll version={version} disabled={!version.mapping || blocking.length > 0 || edited || saving} onMoved={onMoved} />
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" color="secondary" disabled={!edited || saving} onClick={() => setDecisions(initial)}>
              Forkast
            </Button>
            <Button size="small" variant="contained" disabled={!canSave || saving} onClick={handleSave}>
              Lagre kobling
            </Button>
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const MoveAll: FC<{ version: Version; disabled: boolean; onMoved: (result: { count: number; toVersion: string }) => void }> = ({
  version,
  disabled,
  onMoved,
}) => {
  const [migrate, { loading }] = useMutation(MIGRATE_REVISIONS_ON_VERSION);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleMove = async () => {
    setAsking(false);
    setError(null);

    const result = await migrate({
      variables: { input: { fromVersion: version.version } },
      refetchQueries: [{ query: GET_CHECKLIST_VERSIONS_IN_USE }],
    });

    const failure = result.data?.checklistTemplateMigrateRevisions?.errors?.[0];
    if (failure) {
      setError('message' in failure ? failure.message : 'Kunne ikke flytte revisjonene.');
      return;
    }

    const done = result.data?.checklistTemplateMigrateRevisions?.checklistBulkMigration;
    onMoved({ count: done?.migrated ?? 0, toVersion: done?.toVersion ?? '' });
  };

  return (
    <>
      <Button size="small" color="warning" disabled={disabled || loading} onClick={() => setAsking(true)}>
        Flytt {version.revisions} {version.revisions === 1 ? 'revisjon' : 'revisjoner'}
      </Button>

      {error && (
        <Alert severity="error" sx={{ width: '100%' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Dialog open={asking} onClose={() => setAsking(false)}>
        <DialogTitle>Flytte {version.revisions === 1 ? 'revisjonen' : `alle ${version.revisions} revisjonene`}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Revisjonene på versjon {version.version} får aktivitetslistene fra {version.mapping?.toVersion}, og utført arbeid følger koblingen. De
            flyttes samlet, og det finnes ingen vei tilbake.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={() => setAsking(false)}>
            Avbryt
          </Button>
          <Button variant="contained" color="warning" onClick={handleMove}>
            Flytt
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

function targetsByType(template: ChecklistTemplate | null | undefined) {
  const seen = new Set<string>();

  const activities = (template?.lists ?? [])
    .filter((x) => !!x)
    .flatMap((list) =>
      (list.phases ?? [])
        .filter((x) => !!x)
        .flatMap((phase) =>
          (phase.tasks ?? [])
            .filter((x) => !!x)
            .map((task) => ({
              value: [phase.name ?? '', task.name ?? ''].join(sep),
              label: `${phase.name} › ${task.name}`,
            })),
        ),
    )
    .filter((target) => !seen.has(target.value) && !!seen.add(target.value));

  const checkItems = (template?.lists ?? [])
    .flatMap((list) => list?.checkItems ?? [])
    .filter((x) => !!x)
    .map((item) => ({
      value: ['', item.name ?? ''].join(sep),
      label: item.name ?? '',
    }))
    .filter((target) => !seen.has(target.value) && !!seen.add(target.value));

  return { activities, checkItems };
}
